<template>
  <div class="fullscreen-page">
    <HeaderMenu title="Groups" />
    <main class="page-main">
      <!-- Groups List -->
      <div class="groups-list-section">
        <div class="info-card">
          <div class="section-header">
            <h2>Your Groups</h2>
            <button @click="showCreateGroupModal = true" class="primary btn create-btn">
              ＋
            </button>
          </div>
          <div v-if="groups.length === 0" class="empty-state">
            <p>You are not a member of any groups yet.</p>
          </div>
          <div v-else class="groups-grid">
            <div v-for="group in groups" :key="group.id" class="group-card">
              <div class="group-header">
                <div class="group-info">
                  <button 
                    @click="toggleFavorite(group)" 
                    class="favorite-btn"
                    :title="group.is_favorite ? 'Remove from favorites' : 'Add to favorites'"
                  >
                    <span class="star-icon" :class="{ 'star-filled': group.is_favorite }">
                      ★
                    </span>
                  </button>
                  <h3>{{ group.name }}</h3>
                </div>
                <span class="member-count">{{ group.member_count }} member{{ group.member_count !== 1 ? 's' : '' }}</span>
              </div>
              <div class="group-actions">
                <button @click="showInviteModal(group)" class="secondary btn">
                  Invite User
                </button>
                <button @click="leaveGroup(group.id)" class="danger btn">
                  Leave Group
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pending Invitations -->
      <div v-if="pendingInvites.length > 0" class="invites-section">
        <div class="info-card">
          <h2>Pending Invitations</h2>
          <div class="invites-grid">
            <div v-for="invite in pendingInvites" :key="invite.id" class="invite-card">
              <div class="invite-info">
                <h3>{{ invite.group_name }}</h3>
                <p>Invited by: {{ invite.invited_by_username }}</p>
                <p class="invite-date">Invited: {{ formatDate(invite.created_at) }}</p>
              </div>
              <div class="invite-actions">
                <button @click="acceptInvite(invite.id)" class="success btn">
                  Accept
                </button>
                <button @click="declineInvite(invite.id)" class="danger btn">
                  Decline
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Create Group Modal -->
    <Modal v-if="showCreateGroupModal" @close="showCreateGroupModal = false">
      <template #header>
        <h3>Create New Group</h3>
      </template>
      <template #body>
        <div class="form-group">
          <label for="new-group-name">Group Name:</label>
          <input
            v-model="newGroupName"
            id="new-group-name"
            type="text"
            placeholder="Enter group name"
          />
        </div>
      </template>
      <template #footer>
        <div class="modal-actions">
          <button @click="createGroup" class="primary btn" :disabled="creatingGroup">
            {{ creatingGroup ? 'Creating...' : 'Create Group' }}
          </button>
          <button @click="showCreateGroupModal = false" class="secondary btn">
            Cancel
          </button>
        </div>
      </template>
    </Modal>

    <!-- Invite User Modal -->
    <Modal v-if="showInviteModalState" @close="showInviteModalState = false">
      <template #header>
        <h3>Invite User to {{ selectedGroup?.name }}</h3>
      </template>
      <template #body>
        <div class="form-group">
          <label for="invite-username">Username:</label>
          <input
            v-model="inviteUsername"
            id="invite-username"
            type="text"
            placeholder="Enter username to invite"
          />
        </div>
      </template>
      <template #footer>
        <div class="modal-actions">
          <button @click="sendInvite" class="primary btn" :disabled="invitingUser">
            {{ invitingUser ? 'Sending...' : 'Send Invite' }}
          </button>
          <button @click="showInviteModalState = false" class="secondary btn">
            Cancel
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import HeaderMenu from '../components/HeaderMenu.vue'
import Modal from '../components/Modal.vue'
import { API } from '../api/client'
import { useNotifications } from '../composables/useNotifications'

interface GroupWithMembers {
  id: string
  name: string
  created_by: string
  created_at: string
  member_count: number
  is_favorite?: boolean
}

interface PendingInvite {
  id: string
  group_id: string
  group_name: string
  user_id: string
  invited_by: string
  invited_by_username: string
  created_at: string
}

const groups = ref<GroupWithMembers[]>([])
const pendingInvites = ref<PendingInvite[]>([])
const newGroupName = ref('')
const creatingGroup = ref(false)
const showCreateGroupModal = ref(false)
const showInviteModalState = ref(false)
const selectedGroup = ref<GroupWithMembers | null>(null)
const inviteUsername = ref('')
const invitingUser = ref(false)

const { addNotification } = useNotifications()

const handleNavigation = (page: 'tracker' | 'groups' | 'settings') => {
  // Navigation is handled by router, no need for emit
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

const loadGroups = async () => {
  try {
    groups.value = await API.getGroups()
  } catch (error) {
    console.error('Failed to load groups:', error)
    addNotification('Failed to load groups', { type: 'error' })
  }
}

const loadPendingInvites = async () => {
  try {
    pendingInvites.value = await API.getPendingInvites()
  } catch (error) {
    console.error('Failed to load pending invites:', error)
    addNotification('Failed to load pending invites', { type: 'error' })
  }
}

const createGroup = async () => {
  if (!newGroupName.value.trim()) {
    addNotification('Please enter a group name', { type: 'error' })
    return
  }

  creatingGroup.value = true
  try {
    await API.createGroup(newGroupName.value.trim())
    newGroupName.value = ''
    showCreateGroupModal.value = false
    await loadGroups()
    addNotification('Group created successfully!', { type: 'success' })
  } catch (error: any) {
    console.error('Failed to create group:', error)
    addNotification(error.message || 'Failed to create group', { type: 'error' })
  } finally {
    creatingGroup.value = false
  }
}

const toggleFavorite = async (group: GroupWithMembers) => {
  try {
    await API.setFavoriteGroup(group.id)
    // Update the local state immediately for better UX
    groups.value.forEach(g => {
      g.is_favorite = g.id === group.id
    })
    addNotification('Favorite group updated successfully', { type: 'success' })
  } catch (error: any) {
    console.error('Failed to update favorite group:', error)
    addNotification(error.message || 'Failed to update favorite group', { type: 'error' })
  }
}

const leaveGroup = async (groupId: string) => {
  if (!confirm('Are you sure you want to leave this group?')) {
    return
  }

  try {
    await API.leaveGroup(groupId)
    await loadGroups()
    addNotification('Successfully left the group', { type: 'success' })
  } catch (error: any) {
    console.error('Failed to leave group:', error)
    addNotification(error.message || 'Failed to leave group', { type: 'error' })
  }
}

const showInviteModal = (group: GroupWithMembers) => {
  selectedGroup.value = group
  inviteUsername.value = ''
  showInviteModalState.value = true
}

const sendInvite = async () => {
  if (!inviteUsername.value.trim()) {
    addNotification('Please enter a username', { type: 'error' })
    return
  }

  if (!selectedGroup.value) {
    addNotification('No group selected', { type: 'error' })
    return
  }

  invitingUser.value = true
  try {
    await API.inviteUser(selectedGroup.value.id, inviteUsername.value.trim())
    showInviteModalState.value = false
    addNotification('Invite sent successfully!', { type: 'success' })
  } catch (error: any) {
    console.error('Failed to send invite:', error)
    addNotification(error.message || 'Failed to send invite', { type: 'error' })
  } finally {
    invitingUser.value = false
  }
}

const acceptInvite = async (inviteId: string) => {
  try {
    await API.acceptInvite(inviteId)
    await loadGroups()
    await loadPendingInvites()
    addNotification('Successfully joined the group!', { type: 'success' })
  } catch (error: any) {
    console.error('Failed to accept invite:', error)
    addNotification(error.message || 'Failed to accept invite', { type: 'error' })
  }
}

const declineInvite = async (inviteId: string) => {
  try {
    await API.declineInvite(inviteId)
    await loadPendingInvites()
    addNotification('Invite declined', { type: 'success' })
  } catch (error: any) {
    console.error('Failed to decline invite:', error)
    addNotification(error.message || 'Failed to decline invite', { type: 'error' })
  }
}

onMounted(async () => {
  await Promise.all([loadGroups(), loadPendingInvites()])
})
</script>

<style scoped>
.page-main {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.create-group-section,
.groups-list-section,
.invites-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-card {
  background: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
}

.info-card h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #333;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

.form-group input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.group-card {
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.group-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.member-count {
  font-size: 0.9rem;
  color: #666;
  background: #f0f0f0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.group-actions {
  display: flex;
  gap: 0.5rem;
}

.invites-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.invite-card {
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.invite-info h3 {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  color: #333;
}

.invite-info p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
  color: #666;
}

.invite-date {
  font-size: 0.8rem;
  color: #999;
}

.invite-actions {
  display: flex;
  gap: 0.5rem;
}

.empty-state {
  text-align: center;
  color: #666;
  font-style: italic;
}

.modal-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.favorite-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-right: 0.5rem;
}

.star-icon {
  font-size: 1.2rem;
  color: #ddd;
  transition: color 0.2s;
}

.star-icon.star-filled {
  color: #ffd700;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
}

.group-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn {
  text-decoration: none;
  display: inline-block;
}

.message {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.create-btn {
  /* width: 40px;
  height: 40px; */
  display: flex;
  align-items: center;
  justify-content: center;
  /* font-size: 1.2rem; */
  /* padding: 0; */
  padding: 0.125em;
  padding-top: 0em;
  font-size: 1.5em;
}
</style>